import { buildQueryString, request, requestBlob } from './request';

export const companyApi = {
  getCompanyAccess: () => request('/api/company/access'),
  getCompanyPermissionCatalog: () =>
    request('/api/company/permissions').then((res) => ({ permissions: res.permissions || [] })),
  getCompanyRoles: () =>
    request('/api/company/roles').then((res) => ({ roles: res.roles || [] })),
  createCompanyRole: (data) =>
    request('/api/company/roles', { method: 'POST', body: JSON.stringify(data) }),
  updateCompanyRole: (id, data) =>
    request(`/api/company/roles/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteCompanyRole: (id) =>
    request(`/api/company/roles/${id}`, { method: 'DELETE' }),
  updateCompanyRolePermissions: (id, permissionKeys) =>
    request(`/api/company/roles/${id}/permissions`, {
      method: 'PUT',
      body: JSON.stringify({ permission_keys: permissionKeys }),
    }),
  getUserRoles: (id) =>
    request(`/api/company/users/${id}/roles`).then((res) => ({ roles: res.roles || [], user: res.user })),
  updateUserRoles: (id, roleIds) =>
    request(`/api/company/users/${id}/roles`, {
      method: 'PUT',
      body: JSON.stringify({ role_ids: roleIds }),
    }),
  getUsers: (params) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : '';
    return request(`/api/users${qs}`).then((res) => ({
      company_login: res.company_login || null,
      users: res.users || [],
      pagination: res.pagination || { page: 1, totalPages: 0, total: 0 },
    }));
  },
  createUser: (data) => request('/api/users', { method: 'POST', body: JSON.stringify(data) }),
  updateUser: (id, data) => request(`/api/users/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  resetUserPassword: (id, password) =>
    request(`/api/users/${id}/password`, { method: 'PATCH', body: JSON.stringify({ password }) }),
  deleteUser: (id) => request(`/api/users/${id}`, { method: 'DELETE' }),

  getMerchants: (params) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : '';
    return request(`/api/merchants${qs}`).then((res) => ({
      merchants: res.merchants || [],
      pagination: res.pagination || { page: 1, totalPages: 0, total: 0 },
    }));
  },
  getMerchant: (id) => request(`/api/merchants/${id}`),
  getMerchantFinancialReport: (id, params) => request(`/api/merchants/${id}/financial-report${buildQueryString(params)}`),
  getReportDesignTypes: () => request('/api/company/report-designs/types'),
  getReportDesigns: () => request('/api/company/report-designs'),
  getActiveReportDesign: (reportType) => request(`/api/company/report-designs/active/${reportType || 'merchant_shipments'}`),
  saveReportDesign: (data) => request('/api/company/report-designs/save', { method: 'POST', body: JSON.stringify(data) }),
  restoreReportDesignDefault: (data) => request('/api/company/report-designs/restore-default', { method: 'POST', body: JSON.stringify(data) }),
  createMerchant: (data) => request('/api/merchants', { method: 'POST', body: JSON.stringify(data) }),
  updateMerchant: (id, data) => request(`/api/merchants/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  resetMerchantPassword: (id, password) => request(`/api/merchants/${id}/password`, { method: 'PATCH', body: JSON.stringify({ password }) }),
  deleteMerchant: (id) => request(`/api/merchants/${id}`, { method: 'DELETE' }),

  getRiders: (params) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : '';
    return request(`/api/riders${qs}`).then((res) => ({
      riders: res.riders || [],
      branches_active: !!res.branches_active,
      pagination: res.pagination || { page: 1, totalPages: 0, total: 0 },
    }));
  },
  getRider: (id) => request(`/api/riders/${id}`),
  createRider: (data) => request('/api/riders', { method: 'POST', body: JSON.stringify(data) }),
  updateRider: (id, data) => request('/api/riders/' + id, { method: 'PUT', body: JSON.stringify(data) }),
  toggleRider: (id) => request('/api/riders/' + id + '/toggle', { method: 'PATCH' }),
  deleteRider: (id) => request('/api/riders/' + id, { method: 'DELETE' }),

  getVehicleTypes: (params) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : '';
    return request(`/api/vehicle-types${qs}`).then((res) => ({
      vehicle_types: res.vehicle_types || [],
    }));
  },
  createVehicleType: (data) => request('/api/vehicle-types', { method: 'POST', body: JSON.stringify(data) }),
  updateVehicleType: (id, data) => request(`/api/vehicle-types/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteVehicleType: (id) => request(`/api/vehicle-types/${id}`, { method: 'DELETE' }),

  getCompanyVehicles: (params) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : '';
    return request(`/api/company-vehicles${qs}`).then((res) => ({
      vehicles: res.vehicles || [],
    }));
  },
  createCompanyVehicle: (data) => request('/api/company-vehicles', { method: 'POST', body: JSON.stringify(data) }),
  updateCompanyVehicle: (id, data) => request(`/api/company-vehicles/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteCompanyVehicle: (id) => request(`/api/company-vehicles/${id}`, { method: 'DELETE' }),

  getOrders: (params) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : '';
    return request(`/api/orders${qs}`).then((res) => ({
      orders: res.orders || [],
      branches_active: !!res.branches_active,
      pagination: res.pagination || { page: 1, limit: 50, total: 0, totalPages: 0 },
    }));
  },
  getOrder: (id) => request(`/api/orders/${id}`),
  getOrderLabelRendered: (id) => request(`/api/orders/${id}/label-rendered`),
  getOrderShippingQuote: (params) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : '';
    return request(`/api/orders/shipping-quote${qs}`);
  },
  createOrder: (data) => request('/api/orders', { method: 'POST', body: JSON.stringify(data) }),
  validateCompanyBulkOrders: (data) => request('/api/orders/bulk/validate', { method: 'POST', body: JSON.stringify(data) }),
  bulkUploadCompanyOrders: (data) => request('/api/orders/bulk', { method: 'POST', body: JSON.stringify(data) }),
  previewBulkOrderUpdate: (data) =>
    request('/api/orders/bulk-update/preview', { method: 'POST', body: JSON.stringify(data) }),
  applyBulkOrderUpdate: (data) =>
    request('/api/orders/bulk-update', { method: 'POST', body: JSON.stringify(data) }),
  getBulkUpdateMappings: (params = {}) => {
    const qs = params && Object.keys(params).length
      ? '?' + new URLSearchParams(params).toString()
      : '';
    return request(`/api/orders/bulk-update/mappings${qs}`);
  },
  saveBulkUpdateMapping: (data) =>
    request('/api/orders/bulk-update/mappings', { method: 'POST', body: JSON.stringify(data) }),
  deleteBulkUpdateMapping: (id) =>
    request(`/api/orders/bulk-update/mappings/${id}`, { method: 'DELETE' }),
  suggestBulkColumnMapping: (data) =>
    request('/api/company/bulk-upload/suggest-mapping', { method: 'POST', body: JSON.stringify(data) }),
  updateOrder: (id, data) => request(`/api/orders/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  updateOrderStatus: (id, status, note, force = false, options = {}) =>
    request(`/api/orders/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({
        status,
        note,
        force: force ? 1 : 0,
        ...(options.charge_return_fee === false || options.charge_return_fee === 0
          ? { charge_return_fee: 0 }
          : options.charge_return_fee != null
            ? { charge_return_fee: options.charge_return_fee ? 1 : 0 }
            : {}),
      }),
    }),
  batchUpdateOrderStatus: (data) =>
    request('/api/orders/batch-status', { method: 'POST', body: JSON.stringify(data) }),
  batchAssignOrders: (data) =>
    request('/api/orders/batch-assign', { method: 'POST', body: JSON.stringify(data) }),
  archiveOrder: (id) => request(`/api/orders/${id}/archive`, { method: 'PATCH' }),
  unarchiveOrder: (id) => request(`/api/orders/${id}/unarchive`, { method: 'PATCH' }),
  getOrderStats: () => request('/api/orders/stats'),
  exportOrders: (params = {}) => {
    const { format = 'xlsx', ...rest } = params;
    const qs = '?' + new URLSearchParams({ ...rest, format }).toString();
    const ext = format === 'csv' ? 'csv' : 'xlsx';
    return requestBlob(`/api/orders/export${qs}`).then((blob) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `orders-${new Date().toISOString().slice(0, 10)}.${ext}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    });
  },

  getReportsOverview: (params) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : '';
    return request('/api/reports/overview' + qs);
  },
  getReportsRiderPerformance: (params) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : '';
    return request('/api/reports/rider-performance' + qs);
  },
  getReportsMerchantPerformance: (params) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : '';
    return request('/api/reports/merchant-performance' + qs);
  },
  getReportsFinancial: (params) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : '';
    return request('/api/reports/financial' + qs);
  },
  getReportsFilters: () => request('/api/reports/filters'),
  exportReports: (params = {}) => {
    const clean = Object.fromEntries(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ''),
    );
    const qs = Object.keys(clean).length ? `?${new URLSearchParams(clean).toString()}` : '';
    const { format = 'xlsx', type = 'overview' } = clean;
    const ext = format === 'csv' ? 'csv' : 'xlsx';
    return requestBlob(`/api/reports/export${qs}`).then((blob) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `report-${type}-${new Date().toISOString().slice(0, 10)}.${ext}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    });
  },
  getReportsAnalytics: (params) => {
    const clean = Object.fromEntries(
      Object.entries(params || {}).filter(([, v]) => v !== undefined && v !== null && v !== ''),
    );
    const qs = Object.keys(clean).length ? `?${new URLSearchParams(clean).toString()}` : '';
    return request('/api/reports/analytics' + qs);
  },

  getTenantSettings: () => request('/api/tenants/settings'),
  updateTenantSettings: (data) => request('/api/tenants/settings', { method: 'PUT', body: JSON.stringify(data) }),
  getCompanySettings: () => request('/api/company/settings'),

  /** Voice command config (STT + LLM form fill) — company / staff / merchant */
  getVoiceConfig: () => request('/api/voice/config'),
  voiceOrderFill: (data) =>
    request('/api/voice/order-fill', { method: 'POST', body: JSON.stringify(data) }),

  getCompanyWorkflow: () => request('/api/company/workflow'),
  updateCompanyWorkflow: (data) => request('/api/company/workflow', { method: 'PUT', body: JSON.stringify(data) }),
  resetCompanyWorkflow: () => request('/api/company/workflow/reset', { method: 'POST' }),
  getSyncSchedule: () => request('/api/company/sync-schedule'),
  updateSyncSchedule: (data) => request('/api/company/sync-schedule', { method: 'PATCH', body: JSON.stringify(data) }),
  getCompanyOpsResetPreview: () => request('/api/company/ops-reset-preview'),
  resetCompanyOpsData: (data) =>
    request('/api/company/ops-reset', { method: 'POST', body: JSON.stringify(data) }),
  getNotificationPreferences: (options) => request('/api/company/notification-preferences', options),
  updateNotificationPreferences: (data) =>
    request('/api/company/notification-preferences', { method: 'PUT', body: JSON.stringify(data) }),
  getNotificationUsage: (options) => request('/api/company/notification-usage', options),
  getFeatureFlags: () => request('/api/tenant/feature-flags'),

  getShippingSettings: () => request('/api/shipping/settings'),
  getShippingCities: () => request('/api/shipping/cities'),
  createShippingRegion: (data) =>
    request('/api/shipping/regions', { method: 'POST', body: JSON.stringify(data) }),
  updateShippingSettings: (data) => request('/api/shipping/settings', { method: 'PUT', body: JSON.stringify(data) }),
  getShippingRules: (params) => {
    const clean = Object.fromEntries(
      Object.entries(params || {}).filter(([, v]) => v !== undefined && v !== null && v !== '')
    );
    const qs = Object.keys(clean).length ? `?${new URLSearchParams(clean).toString()}` : '';
    return request(`/api/shipping/rules${qs}`).then((res) => ({
      rules: res.rules || [],
      pagination: res.pagination || { page: 1, limit: 25, total: 0, totalPages: 0 },
    }));
  },
  createShippingRule: (data) => request('/api/shipping/rules', { method: 'POST', body: JSON.stringify(data) }),
  updateShippingRule: (id, data) => request(`/api/shipping/rules/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  toggleShippingRule: (id) => request(`/api/shipping/rules/${id}/toggle`, { method: 'PATCH' }),
  deleteShippingRule: (id) => request(`/api/shipping/rules/${id}`, { method: 'DELETE' }),
  batchUpdateShippingRules: (body) => request('/api/shipping/rules/batch', { method: 'PATCH', body: JSON.stringify(body) }),

  getRiderMonitoring: () => request('/api/riders/monitoring'),
  getRiderGps: () => request('/api/riders/gps'),

  getTenantCharts: () => request('/api/tenant/charts'),
  getEarningsDashboard: (params) => {
    const clean = Object.fromEntries(
      Object.entries(params || {}).filter(([, v]) => v !== undefined && v !== null && v !== ''),
    );
    const qs = Object.keys(clean).length ? `?${new URLSearchParams(clean).toString()}` : '';
    return request(`/api/reports/earnings-dashboard${qs}`);
  },
  exportEarningsDashboard: (params = {}) => {
    const { format = 'xlsx', type = 'merchants', ...rest } = params;
    const qs = '?' + new URLSearchParams({ ...rest, format, type }).toString();
    const ext = format === 'csv' ? 'csv' : 'xlsx';
    const prefix = type === 'branches' ? 'branch-earnings' : 'merchant-earnings';
    return requestBlob(`/api/reports/earnings-dashboard/export${qs}`).then((blob) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${prefix}-${new Date().toISOString().slice(0, 10)}.${ext}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    });
  },

  getManagedBranches: () => request('/api/branches/managed'),

  getBranches: (params) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : '';
    return request(`/api/branches${qs}`).then((res) => ({
      branches: res.branches || [],
      branches_active: !!res.branches_active,
      pagination: res.pagination || { page: 1, totalPages: 0, total: 0 },
    }));
  },
  getBranch: (id) => request(`/api/branches/${id}`),
  getBranchDashboard: (id) => request(`/api/branches/${id}/dashboard`),
  updateBranchPermissions: (id, admin_permissions) =>
    request(`/api/branches/${id}/permissions`, { method: 'PATCH', body: JSON.stringify({ admin_permissions }) }),
  createBranch: (data) => request('/api/branches', { method: 'POST', body: JSON.stringify(data) }),
  updateBranch: (id, data) => request(`/api/branches/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  updateBranchManager: (id, data) => request(`/api/branches/${id}/manager`, { method: 'PATCH', body: JSON.stringify(data) }),
  toggleBranch: (id) => request(`/api/branches/${id}/toggle`, { method: 'PATCH' }),
  getBranchEmployees: (id) => request(`/api/branches/${id}/employees`),
  assignBranchEmployee: (id, userId) => request(`/api/branches/${id}/employees`, { method: 'POST', body: JSON.stringify({ user_id: userId }) }),
  removeBranchEmployee: (id, userId) => request(`/api/branches/${id}/employees/${userId}`, { method: 'DELETE' }),
  getBranchRiders: (id) => request(`/api/branches/${id}/riders`),
  assignBranchRider: (id, riderId) => request(`/api/branches/${id}/riders`, { method: 'POST', body: JSON.stringify({ rider_id: riderId }) }),
  removeBranchRider: (id, riderId) => request(`/api/branches/${id}/riders/${riderId}`, { method: 'DELETE' }),
  getBranchStats: (id) => request(`/api/branches/${id}/stats`),

  getDispatchDashboard: () => request('/api/dispatch/dashboard'),
  getDispatchRider: (id) => request(`/api/dispatch/riders/${id}`),
  dispatchAssign: (orderId, riderId) =>
    request('/api/dispatch/assign', { method: 'POST', body: JSON.stringify({ order_id: orderId, rider_id: riderId }) }),

  getRiderSettlementSummary: (params) => request(`/api/rider-settlements/summary${buildQueryString(params)}`),
  getRiderSettlementOrders: (params) => request(`/api/rider-settlements/orders${buildQueryString(params)}`),
  getRiderSettlementHistory: (params) => request(`/api/rider-settlements/history${buildQueryString(params)}`),
  getRiderSettlementDetail: (id) => request(`/api/rider-settlements/${id}`),
  processRiderSettlement: (body) => request('/api/rider-settlements', { method: 'POST', body: JSON.stringify(body) }),

  getMerchantPayoutSummary: (params) => request(`/api/merchant-payouts/summary${buildQueryString(params)}`),
  getMerchantPayoutOrders: (params) => request(`/api/merchant-payouts/orders${buildQueryString(params)}`),
  getMerchantPayoutHistory: (params) => request(`/api/merchant-payouts/history${buildQueryString(params)}`),
  getMerchantPayoutDetail: (id) => request(`/api/merchant-payouts/${id}`),
  processMerchantPayout: (body) => request('/api/merchant-payouts', { method: 'POST', body: JSON.stringify(body) }),
  getMerchantPayoutActive: (params) => request(`/api/merchant-payouts/active${buildQueryString(params)}`),
  updateMerchantPayout: (id, body) => request(`/api/merchant-payouts/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  sendMerchantPayout: (id) => request(`/api/merchant-payouts/${id}/send`, { method: 'POST', body: JSON.stringify({}) }),
  cancelMerchantPayout: (id) => request(`/api/merchant-payouts/${id}/cancel`, { method: 'POST', body: JSON.stringify({}) }),

  getExpenseTypes: (params) => request(`/api/expense-types${buildQueryString(params)}`),
  createExpenseType: (body) => request('/api/expense-types', { method: 'POST', body: JSON.stringify(body) }),
  updateExpenseType: (id, body) => request(`/api/expense-types/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  deleteExpenseType: (id) => request(`/api/expense-types/${id}`, { method: 'DELETE' }),

  getExpenses: (params) => request(`/api/expenses${buildQueryString(params)}`),
  createExpense: (body) => request('/api/expenses', { method: 'POST', body: JSON.stringify(body) }),
  updateExpense: (id, body) => request(`/api/expenses/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  deleteExpense: (id) => request(`/api/expenses/${id}`, { method: 'DELETE' }),
  getExpenseReportSummary: (params) => request(`/api/expenses/report/summary${buildQueryString(params)}`),
  getExpenseReportPie: (params) => request(`/api/expenses/report/pie${buildQueryString(params)}`),
  getExpenseReportTimeline: (params) => request(`/api/expenses/report/timeline${buildQueryString(params)}`),

  getShipmentApprovalEnabled: () => request('/api/shipment-approval/enabled'),
  getShipmentApprovalGroups: (params) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : '';
    return request(`/api/shipment-approval/groups${qs}`);
  },
  getShipmentApprovalShipments: (params) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : '';
    return request(`/api/shipment-approval/shipments${qs}`);
  },
  decideShipmentApproval: (body) =>
    request('/api/shipment-approval/decide', { method: 'POST', body: JSON.stringify(body) }),
  exportShipmentApproval: (params = {}) => {
    const qs = '?' + new URLSearchParams(params).toString();
    return requestBlob(`/api/shipment-approval/export${qs}`).then((blob) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `pending-approval-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    });
  },
};